import { useState } from 'react'
import { usePostHog } from 'posthog-js/react'

import styles from '@/pages/app/Settings/styles.module.scss'

import { isApiError } from '@/api'
import { enableNotetakerEarlyAccess } from '@/api/enterprise'
import InfoIcon from '@/assets/icons/info.svg?react'
import ConfirmDialog from '@/components/ConfirmDialog'
import CtaPill from '@/components/CtaPill'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { DPA_URL, MSA_URL } from '@/constants/legal'
import PermissionTooltip from '@/pages/app/Settings/PermissionTooltip'
import { EnterpriseSettings, SettingType } from '@/types'

const ENABLED_ID = 'enabled'
const DISABLED_ID = 'disabled'

const NOTETAKER_TOOLTIP = 'Controls whether Notetaker is enabled for your organization.'
const NOTETAKER_MANAGED_TOOLTIP = 'This setting is managed by Wispr'

const NOTETAKER_OPTIONS = [
  {
    id: ENABLED_ID,
    title: 'Enabled for everyone',
    helperText: 'Members can use Notetaker',
  },
  {
    id: DISABLED_ID,
    title: 'Disabled for everyone',
    helperText: 'Disables Notetaker for your organization.',
  },
] as const

const COMING_SOON_ENABLED_TITLE = 'Auto-enables Sept 30'
const COMING_SOON_ENABLED_HELPER = 'Members can use Notetaker starting Sept 30'

const getNotetakerDropdownItems = (allowNotetaker: boolean, comingSoon: boolean): DropdownItem[] => {
  const currentId = allowNotetaker ? ENABLED_ID : DISABLED_ID
  return NOTETAKER_OPTIONS.map((option) => ({
    ...option,
    ...(comingSoon && option.id === ENABLED_ID
      ? { title: COMING_SOON_ENABLED_TITLE, helperText: COMING_SOON_ENABLED_HELPER }
      : {}),
    selected: option.id === currentId,
  }))
}

// Items derive from allowNotetaker each render, so Dropdown's setItems is inert.
const noopSetItems = () => undefined

const DISABLE_MESSAGE = 'This disables Notetaker for your organization.'
const HIPAA_SWAP_MESSAGE =
  'Notetaker is not currently compatible with HIPAA and your BAA. Enabling it will disable org-wide HIPAA for your organization.'

const EARLY_ACCESS_MESSAGE =
  'Notetaker will be enabled for your organization on September 30, 2026, unless you disable it here before then.'
const EARLY_ACCESS_ENABLED_MESSAGE = 'Notetaker is now enabled for your organization.'
const EARLY_ACCESS_CONFIRM_MESSAGE = (
  <>
    <p className={styles.earlyAccessConfirmMessage}>
      By enabling Notetaker, you acknowledge that you accept the changes to our{' '}
      <a href={MSA_URL} target="_blank" rel="noopener noreferrer">
        MSA
      </a>{' '}
      and{' '}
      <a href={DPA_URL} target="_blank" rel="noopener noreferrer">
        DPA
      </a>{' '}
      and that your company (i) understands and agrees that Notetaker data (not dictation data) will be stored in the
      Wispr cloud, notwithstanding any previous Privacy Mode election you may have made for dictation, (ii) will provide
      all necessary notices, and obtain all necessary rights, permissions, and consents relating to the recording,
      monitoring, transcription, or other processing of communications in connection with its use of the Wispr service,
      including Notetaker, and (iii) will comply with all laws that are applicable to use of the Wispr service. This
      confirmation amends the terms of the Master Services Agreement or other main agreement in place between your
      company and Wispr.
    </p>
    <p className={styles.earlyAccessConfirmMessage}>
      Users will have to quit and reopen the app for the changes to take effect.
    </p>
  </>
)

interface NotetakerControlsSectionProps {
  orgName: string
  isAdmin: boolean
  allowNotetaker: boolean
  hasEntitlementOverrides: boolean
  applySettingsPatch: (patch: Partial<EnterpriseSettings>) => Promise<void>
  isSaving?: boolean
  enablingRevokesBaa: boolean
  isDisablingHipaaForNotetaker: boolean
  onDisableHipaaAndEnableNotetaker: () => Promise<void>
  showEarlyAccessHelper?: boolean
}

const NotetakerControlsSection = ({
  orgName,
  isAdmin,
  allowNotetaker,
  hasEntitlementOverrides,
  applySettingsPatch,
  isSaving = false,
  enablingRevokesBaa,
  isDisablingHipaaForNotetaker,
  onDisableHipaaAndEnableNotetaker,
  showEarlyAccessHelper = false,
}: NotetakerControlsSectionProps) => {
  const posthog = usePostHog()
  const { updateToast } = useToast()
  const [pendingDisable, setPendingDisable] = useState(false)
  const [pendingHipaaSwap, setPendingHipaaSwap] = useState(false)
  const [pendingEarlyAccess, setPendingEarlyAccess] = useState(false)
  const [earlyAccessState, setEarlyAccessState] = useState<'idle' | 'pending' | 'enabled'>('idle')
  const showHelper = showEarlyAccessHelper && allowNotetaker
  const items = getNotetakerDropdownItems(allowNotetaker, showHelper && earlyAccessState !== 'enabled')

  const handleEnableEarlyAccess = async () => {
    setEarlyAccessState('pending')
    try {
      await enableNotetakerEarlyAccess()
      setEarlyAccessState('enabled')
      posthog?.reloadFeatureFlags()
    } catch (error) {
      setEarlyAccessState('idle')
      updateToast(isApiError(error) ? error.message : 'Failed to enable Notetaker', 'error')
    }
  }

  const handleSelect = async (selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0) return

    const nextEnabled = selectedItems[0].id === ENABLED_ID
    if (nextEnabled === allowNotetaker) return

    if (!nextEnabled) {
      setPendingDisable(true)
      return
    }

    if (enablingRevokesBaa) {
      setPendingHipaaSwap(true)
      return
    }

    await applySettingsPatch({ [SettingType.allowNotetaker]: true })
    // Until GA, offer immediate access on top of the just-saved enabled-at-GA state.
    if (showEarlyAccessHelper) {
      setPendingEarlyAccess(true)
    }
  }

  const dropdown = (
    <Dropdown
      items={items}
      setItems={noopSetItems}
      onSelect={handleSelect}
      placeholder="Select policy"
      className={styles.dataControlDropdown}
      disabled={!isAdmin || hasEntitlementOverrides}
      ariaLabel="Notetaker"
    />
  )

  return (
    <>
      <div
        className={`${styles.settingsItem} ${showHelper ? styles.alignTop : ''}`}
        data-testid="notetaker-allow-setting"
      >
        <div className={styles.labelBlock}>
          <div className={styles.toggleLabel}>
            Notetaker
            <Tooltip text={NOTETAKER_TOOLTIP} placement="top" hideArrow>
              <InfoIcon className={styles.infoIcon} />
            </Tooltip>
          </div>
          {showHelper && (
            <div
              className={styles.earlyAccessHelper}
              data-testid="notetaker-early-access-helper"
              role="status"
              aria-live="polite"
            >
              {earlyAccessState === 'enabled' ? (
                <div className={styles.labelDescription}>{EARLY_ACCESS_ENABLED_MESSAGE}</div>
              ) : (
                <>
                  <div className={styles.labelDescription}>{EARLY_ACCESS_MESSAGE}</div>
                  {isAdmin && (
                    <div className={styles.upgradeCta}>
                      Or you can
                      <CtaPill
                        label="Enable Notetaker now"
                        onClick={() => setPendingEarlyAccess(true)}
                        disabled={earlyAccessState === 'pending'}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {hasEntitlementOverrides ? (
          <Tooltip text={NOTETAKER_MANAGED_TOOLTIP} placement="top" hideArrow>
            {dropdown}
          </Tooltip>
        ) : (
          <PermissionTooltip>{dropdown}</PermissionTooltip>
        )}
      </div>

      <ConfirmDialog
        isOpen={pendingDisable}
        title={`Disable Notetaker for ${orgName}?`}
        message={DISABLE_MESSAGE}
        confirmLabel="Disable Notetaker"
        confirmType="destructive"
        isConfirming={isSaving}
        onConfirm={async () => {
          try {
            await applySettingsPatch({ [SettingType.allowNotetaker]: false })
          } finally {
            setPendingDisable(false)
          }
        }}
        onCancel={() => setPendingDisable(false)}
      />

      <ConfirmDialog
        isOpen={pendingHipaaSwap}
        title="Enabling Notetaker will revoke your BAA"
        message={HIPAA_SWAP_MESSAGE}
        confirmLabel="Revoke BAA"
        confirmType="destructive"
        isConfirming={isDisablingHipaaForNotetaker}
        onConfirm={async () => {
          try {
            await onDisableHipaaAndEnableNotetaker()
          } finally {
            setPendingHipaaSwap(false)
          }
        }}
        onCancel={() => setPendingHipaaSwap(false)}
      />

      <ConfirmDialog
        isOpen={pendingEarlyAccess}
        title={`Enable Notetaker for ${orgName} now?`}
        message={EARLY_ACCESS_CONFIRM_MESSAGE}
        confirmLabel="Confirm"
        confirmType="dark"
        isConfirming={earlyAccessState === 'pending'}
        onConfirm={async () => {
          try {
            await handleEnableEarlyAccess()
          } finally {
            setPendingEarlyAccess(false)
          }
        }}
        onCancel={() => setPendingEarlyAccess(false)}
      />
    </>
  )
}

export default NotetakerControlsSection
